import {
  applyTemplate,
  asFiles,
  projectId as newProjectId,
  ProjectDoc,
  SYSTEM_ORIGIN,
  type ColorName,
  type FileMeta,
  type StarterCollection,
  type StarterSection,
  type TemplateId,
} from '@tomo/core';
import type { ApiProject, ApiWorkspace } from '@tomo/core/api';
import { toast } from '@tomo/ui';
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { clearDocument, IndexeddbPersistence, storeState } from 'y-indexeddb';
import * as Y from 'yjs';
import { Store } from '../lib/store.ts';
import { getBlob, ingestGenerated, putBlob } from './blobs.ts';
import { requestPersistentStorage, type LocalProjectEntry } from './db.ts';
import { getIdentity, identityStore } from './identity.ts';
import { getRegistryEntry, patchRegistry, removeFromRegistry, upsertRegistry } from './registry.ts';

/**
 * A ProjectSession is one open project: its Y.Doc, local persistence (IndexedDB, always on — this
 * is what makes the free tier work offline) and, for cloud projects, a live sync connection.
 * Sessions are ref-counted and kept warm briefly after the last user leaves.
 */

export type SyncStatus = 'connecting' | 'synced' | 'syncing' | 'offline' | 'signed-out' | 'upgrade-required' | 'forbidden' | 'error';

export type SyncState =
  | { mode: 'local' }
  | { mode: 'cloud'; status: SyncStatus; readOnly: boolean; unsynced: number; message?: string };

export interface PresenceUser {
  clientId: number;
  id: string;
  name: string;
  color: string;
  /** Record/page the person is looking at. */
  location: string | null;
  self: boolean;
}

/** Implemented by the cloud module; attached to sessions of cloud projects. */
export interface CloudConnection {
  destroy(): void;
  setLocation(location: string | null): void;
  readonly awareness: import('y-protocols/awareness').Awareness | null;
}

export const docDbName = (id: string) => `tomo:project:${id}`;

type CloudAttacher = (session: ProjectSession) => CloudConnection;
let cloudAttacher: CloudAttacher | null = null;
let cloudAuthKey = '';
/**
 * The cloud module registers how to connect cloud projects (keeps this module cloud-agnostic).
 * `authKey` identifies the account state (who is signed in, their plans and roles): open cloud
 * sessions reconnect whenever it changes, e.g. after signing in, upgrading or a role change.
 */
export function setCloudAttacher(fn: CloudAttacher | null, authKey = '') {
  cloudAttacher = fn;
  cloudAuthKey = authKey;
  for (const s of sessions.values()) s.refreshCloud();
}

let lastViewOnlyToast = 0;
function notifyViewOnly() {
  if (Date.now() - lastViewOnlyToast < 4000) return;
  lastViewOnlyToast = Date.now();
  toast({ title: 'View only', description: 'You can view this project but not change it. Ask a workspace admin for edit access.' });
}

export class ProjectSession {
  readonly id: string;
  readonly doc: Y.Doc;
  readonly project: ProjectDoc;
  readonly persistence: IndexeddbPersistence;
  readonly ready: Promise<void>;
  readonly sync = new Store<SyncState>({ mode: 'local' });
  readonly presence = new Store<PresenceUser[]>([]);
  cloud: CloudConnection | null = null;
  refs = 0;
  loaded = false;
  private cloudKey: string | null = null;
  private disposers: (() => void)[] = [];
  private registryTimer: ReturnType<typeof setTimeout> | undefined;
  private destroyed = false;

  constructor(id: string) {
    this.id = id;
    this.doc = new Y.Doc({ gc: true });
    this.project = new ProjectDoc(this.doc);
    this.project.onReadOnlyWrite = notifyViewOnly;
    this.persistence = new IndexeddbPersistence(docDbName(id), this.doc);
    this.ready = this.persistence.whenSynced.then(() => this.afterLoad());
  }

  private afterLoad() {
    if (this.destroyed) return;
    this.loaded = true;
    // Before anything writes (identity below): viewers must not fork their copy, even offline.
    this.project.setReadOnly(getRegistryEntry(this.id)?.cloud?.role === 'viewer');
    this.applyIdentity();
    this.disposers.push(identityStore.subscribe(() => this.applyIdentity()));
    this.disposers.push(
      this.project.subscribe(() => {
        clearTimeout(this.registryTimer);
        this.registryTimer = setTimeout(() => this.syncRegistry(), 600);
      }),
    );
    this.refreshCloud();
  }

  private applyIdentity() {
    const me = getIdentity();
    if (me.id === 'pending') return;
    this.project.actorId = me.id;
    if (!this.project.isInitialized()) return;
    if (me.kind === 'account' && me.localId !== me.id && this.project.getPerson(me.localId)) {
      this.project.replaceActorId(me.localId, me.id);
    }
    this.project.upsertPerson({ id: me.id, name: me.name, color: me.color });
  }

  /** Connect, reconnect or disconnect cloud sync to match the registry entry and account state. */
  refreshCloud() {
    if (!this.loaded || this.destroyed) return;
    const entry = getRegistryEntry(this.id);
    const key = entry?.cloud && cloudAttacher ? `${entry.cloud.workspaceId}|${cloudAuthKey}` : null;
    if (key === this.cloudKey) return;
    this.cloudKey = key;
    this.cloud?.destroy();
    this.cloud = null;
    this.presence.set([]);
    if (key) this.cloud = cloudAttacher!(this);
    else if (!entry?.cloud) {
      this.project.setReadOnly(false);
      this.sync.set({ mode: 'local' });
    }
  }

  /** Re-stamp the local user into the doc (e.g. after gaining edit rights). */
  refreshIdentity() {
    if (this.loaded && !this.destroyed) this.applyIdentity();
  }

  /** Page/record the user is on, shared with collaborators (kept across reconnects). */
  location: string | null = null;
  setLocation(location: string | null) {
    this.location = location;
    this.cloud?.setLocation(location);
  }

  private async syncRegistry() {
    if (this.destroyed || !this.project.isInitialized()) return;
    const meta = this.project.getMeta();
    const collections = this.project.listCollections();
    const records = this.project.allRecords().length;
    await patchRegistry(this.id, {
      name: meta.name,
      description: meta.description,
      icon: meta.icon,
      color: meta.color,
      updatedAt: Date.now(),
      stats: { collections: collections.length, records },
    });
  }

  retain() {
    this.refs++;
  }

  release(): number {
    return --this.refs;
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    clearTimeout(this.registryTimer);
    this.disposers.forEach((d) => d());
    this.cloud?.destroy();
    this.cloud = null;
    this.project.destroy();
    void this.persistence.destroy();
    this.doc.destroy();
  }
}

const sessions = new Map<string, ProjectSession>();
const releaseTimers = new Map<string, ReturnType<typeof setTimeout>>();

export function acquireProject(id: string): ProjectSession {
  clearTimeout(releaseTimers.get(id));
  releaseTimers.delete(id);
  let s = sessions.get(id);
  if (!s) {
    s = new ProjectSession(id);
    sessions.set(id, s);
  }
  s.retain();
  return s;
}

export function releaseProject(session: ProjectSession) {
  if (session.release() > 0) return;
  releaseTimers.set(
    session.id,
    setTimeout(() => {
      if (session.refs > 0) return;
      session.destroy();
      if (sessions.get(session.id) === session) sessions.delete(session.id);
      releaseTimers.delete(session.id);
    }, 30_000),
  );
}

export function getOpenSession(id: string): ProjectSession | undefined {
  return sessions.get(id);
}

/* ------------------------------------------------------------------------------ create / delete */

export interface CreateProjectInput {
  name: string;
  template: TemplateId;
  description?: string;
  icon?: string;
  color?: ColorName;
  /** A blank project's own collections, created ahead of the ready-made ones. */
  collections?: StarterCollection[];
  /** The ready-made parts a blank project starts with (all of them when left out). */
  sections?: readonly StarterSection[];
  id?: string;
  cloud?: { workspaceId: string } | null;
}

export async function createProject(input: CreateProjectInput): Promise<string> {
  const id = input.id ?? newProjectId();
  const session = acquireProject(id);
  try {
    await session.ready;
    const me = getIdentity();
    session.project.actorId = me.id;
    session.project.initialize({
      id,
      name: input.name,
      description: input.description ?? '',
      icon: input.icon ?? (input.template === 'sample' ? 'i:flame' : 'i:gem'),
      color: input.color ?? 'yellow',
      template: input.template,
    });
    session.project.upsertPerson({ id: me.id, name: me.name, color: me.color });
    await applyTemplate(session.project, input.template, { putFile: ingestGenerated, me: { id: me.id, name: me.name, color: me.color }, collections: input.collections, sections: input.sections });
    // Persist a full snapshot now instead of trusting the async per-update writes to finish.
    await storeState(session.persistence, true);
    const now = Date.now();
    const meta = session.project.getMeta();
    await upsertRegistry({
      id,
      name: meta.name,
      description: meta.description,
      icon: meta.icon,
      color: meta.color,
      template: input.template,
      createdAt: now,
      updatedAt: now,
      openedAt: now,
      cloud: input.cloud ?? null,
      stats: { collections: session.project.listCollections().length, records: session.project.allRecords().length },
    });
    void requestPersistentStorage();
    return id;
  } finally {
    releaseProject(session);
  }
}

/** Emberfall, the sample project. A device with no projects starts here. */
export function createSampleProject(): Promise<string> {
  return createProject({
    name: 'Emberfall',
    description: 'A fantasy RTS with three factions — a sample project that shows off everything Tomo can do.',
    template: 'sample',
    icon: 'i:flame',
    color: 'orange',
  });
}

/** List a cloud project on this device so it can be opened; its content downloads on first open. */
export async function addCloudProject(project: ApiProject, workspace: Pick<ApiWorkspace, 'id' | 'role'>): Promise<void> {
  if (getRegistryEntry(project.id)) return;
  await upsertRegistry({
    id: project.id,
    name: project.name,
    description: '',
    icon: project.icon ?? 'i:gem',
    color: (project.color as LocalProjectEntry['color']) ?? 'yellow',
    template: 'cloud',
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    openedAt: Date.now(),
    cloud: { workspaceId: workspace.id, role: workspace.role },
  });
}

export async function markOpened(id: string) {
  await patchRegistry(id, { openedAt: Date.now() });
}

/** Remove a project and its data from this device (cloud copies are untouched). */
export async function deleteLocalProject(id: string) {
  const s = sessions.get(id);
  if (s) {
    s.destroy();
    sessions.delete(id);
  }
  await clearDocument(docDbName(id));
  await removeFromRegistry(id);
}

/* ------------------------------------------------------------------------------ files referenced by a project */

/**
 * Every blob the project refers to, thumbnails included: record covers and file fields, pictures
 * placed on a board, and pictures placed in a document body. Anything missed here would sync or
 * export as a reference with no bytes behind it, so this has to see all four.
 *
 * Trashed collections, records and boards count, since they can still be restored.
 */
export function projectFileHashes(project: ProjectDoc): Set<string> {
  const out = new Set<string>();
  const add = (meta: FileMeta | null | undefined) => {
    if (!meta) return;
    out.add(meta.hash);
    if (meta.thumb) out.add(meta.thumb);
  };
  for (const c of project.listCollections(true)) {
    const fileFields = c.fields.filter((f) => f.type === 'file');
    for (const r of project.listRecords(c.id, true)) {
      add(r.cover);
      for (const f of fileFields) for (const meta of asFiles(r.values[f.id])) add(meta);
      for (const hash of project.getRecordText(r.id).images) out.add(hash);
    }
  }
  for (const b of [...project.listBoards(), ...project.trashedBoards()]) {
    for (const item of project.listBoardItems(b.id)) add(item.file);
  }
  return out;
}

/* ------------------------------------------------------------------------------ .tomo archives */

interface ArchiveManifest {
  format: 'tomo-project';
  version: 1;
  exportedAt: number;
  project: { id: string; name: string; icon: string; color: string; template: string };
  blobs: { hash: string; mime: string }[];
}

/** Export a whole project (document + files) as a single `.tomo` zip. */
export async function exportProjectArchive(session: ProjectSession): Promise<Blob> {
  await session.ready;
  const meta = session.project.getMeta();
  const files: Record<string, Uint8Array> = {};
  const blobs: ArchiveManifest['blobs'] = [];
  const hashes = projectFileHashes(session.project);
  for (const hash of hashes) {
    const blob = await getBlob(hash);
    if (!blob) continue;
    files[`blobs/${hash}`] = new Uint8Array(await blob.arrayBuffer());
    blobs.push({ hash, mime: blob.type || 'application/octet-stream' });
  }
  const manifest: ArchiveManifest = {
    format: 'tomo-project',
    version: 1,
    exportedAt: Date.now(),
    project: { id: meta.id, name: meta.name, icon: meta.icon, color: meta.color, template: meta.template },
    blobs,
  };
  files['tomo.json'] = strToU8(JSON.stringify(manifest, null, 2));
  files['project.ydoc'] = Y.encodeStateAsUpdate(session.doc);
  const zipped = zipSync(files, { level: 6 });
  return new Blob([zipped as BlobPart], { type: 'application/zip' });
}

/** Import a `.tomo` archive as a new local project; returns the new id. */
export async function importProjectArchive(file: Blob): Promise<string> {
  const entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const manifestRaw = entries['tomo.json'];
  const state = entries['project.ydoc'];
  if (!manifestRaw || !state) throw new Error('This file is not a Tomo project archive.');
  const manifest = JSON.parse(strFromU8(manifestRaw)) as ArchiveManifest;
  if (manifest.format !== 'tomo-project') throw new Error('Unsupported archive format.');
  for (const b of manifest.blobs) {
    const bytes = entries[`blobs/${b.hash}`];
    if (bytes) await putBlob(bytes, b.mime);
  }
  const id = newProjectId();
  const session = acquireProject(id);
  try {
    await session.ready;
    Y.applyUpdate(session.doc, state, SYSTEM_ORIGIN);
    // Imported copies get their own identity so they never collide with the original.
    session.doc.transact(() => {
      session.project.meta.set('id', id);
      const existing = getRegistryEntry(manifest.project.id);
      if (existing) session.project.meta.set('name', `${session.project.getMeta().name} (imported)`);
    }, SYSTEM_ORIGIN);
    await storeState(session.persistence, true);
    const meta = session.project.getMeta();
    const now = Date.now();
    const entry: LocalProjectEntry = {
      id,
      name: meta.name,
      description: meta.description,
      icon: meta.icon,
      color: meta.color,
      template: meta.template,
      createdAt: meta.createdAt || now,
      updatedAt: now,
      openedAt: now,
      cloud: null,
      stats: { collections: session.project.listCollections().length, records: session.project.allRecords().length },
    };
    await upsertRegistry(entry);
    return id;
  } finally {
    releaseProject(session);
  }
}
